import React, { useState, useEffect } from 'react';
import {
  Card, Button, Space, Table, Tag, message,
  Popconfirm, Tooltip, Switch, TimePicker, Divider, Alert
} from 'antd';
import dayjs from 'dayjs';
import {
  DatabaseOutlined, DownloadOutlined,
  DeleteOutlined, ReloadOutlined, SaveOutlined,
  HistoryOutlined, CheckCircleOutlined,
  FileTextOutlined, CloudDownloadOutlined, CloudUploadOutlined,
  CloudServerOutlined
} from '@ant-design/icons';
import { apiClient } from '@/services/api';

interface BackupFile {
  filename: string;
  size: number;
  createdAt: string;
  type: 'auto' | 'manual';
  location?: 'local' | 'cloudflare' | 'tencent';
  backupType?: 'database' | 'site'; // 备份类型：数据库或站点
  notes?: string; // 备份备注
  status?: 'processing' | 'completed'; // 备份状态
}

const DatabaseManager: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [backups, setBackups] = useState<BackupFile[]>([]);
  const [dbStats, setDbStats] = useState<any>(null);
  
  // 统一备份目标（站点和数据库共用）
  const [backupTarget, setBackupTarget] = useState<string[]>(['local', 'cloudflare']);
  
  // 站点备份状态
  const [siteAutoBackupEnabled, setSiteAutoBackupEnabled] = useState(true);
  const [siteAutoBackupInterval, setSiteAutoBackupInterval] = useState('weekly');
  const [siteAutoBackupTime, setSiteAutoBackupTime] = useState('03:00');
  const [siteAutoBackupWeekday, setSiteAutoBackupWeekday] = useState(0); // 0=周日, 1=周一, ..., 6=周六
  const [siteBackupIncludeEnv, setSiteBackupIncludeEnv] = useState(false);
  const [siteBackupNotes, setSiteBackupNotes] = useState('');
  
  // 数据库备份状态
  const [dbAutoBackupEnabled, setDbAutoBackupEnabled] = useState(true);
  const [dbAutoBackupInterval, setDbAutoBackupInterval] = useState('daily');
  const [dbAutoBackupTime, setDbAutoBackupTime] = useState('02:00');
  const [dbAutoBackupWeekday, setDbAutoBackupWeekday] = useState(0); // 0=周日, 1=周一, ..., 6=周六

  // 获取数据库统计
  const fetchDbStats = async () => {
    try {
      const response = await apiClient.get('/admin/database/stats');
      if (response.success) {
        setDbStats(response.data);
      }
    } catch (error: any) {
      console.error('获取数据库统计失败:', error);
    }
  };

  // 刷新所有数据（带防抖）
  const handleRefresh = async () => {
    if (loading) return; // 防止重复点击
    
    setLoading(true);
    try {
      await Promise.all([
        fetchDbStats(),
        fetchBackups()
      ]);
      message.success('数据已刷新');
    } catch (error) {
      console.error('刷新失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取备份列表（包括数据库和站点备份）
  const fetchBackups = async () => {
    let dbBackups: BackupFile[] = [];
    let siteBackups: BackupFile[] = [];
    
    // 获取数据库备份（容错处理）
    try {
      const dbResponse = await apiClient.get('/admin/database/backups');
      if (dbResponse.success && dbResponse.data?.backups) {
        dbBackups = dbResponse.data.backups.map((b: any) => ({
          ...b,
          backupType: 'database' as const
        }));
        console.log('数据库备份列表:', dbBackups);
      }
    } catch (error: any) {
      console.error('获取数据库备份列表失败:', error);
    }
    
    // 获取站点备份（容错处理）
    try {
      const siteResponse = await apiClient.get('/admin/site/backups');
      if (siteResponse.success && siteResponse.data?.backups) {
        siteBackups = siteResponse.data.backups.map((b: any) => ({
          ...b,
          backupType: 'site' as const
        }));
        console.log('站点备份列表:', siteBackups);
      }
    } catch (error: any) {
      // 站点备份API可能还未实现，不影响数据库备份的显示
      console.warn('获取站点备份列表失败（API可能未实现）:', error.message);
    }
    
    // 合并并按时间排序
    const allBackups = [...dbBackups, ...siteBackups].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    
    console.log('合并后的备份列表:', allBackups);
    setBackups(allBackups);
  };

  // 站点级备份（后台异步执行）
  const handleSiteBackup = async () => {
    try {
      if (backupTarget.length === 0) {
        message.warning('请先选择备份目标');
        return;
      }
      
      setLoading(true);
      const response = await apiClient.post('/admin/site/backup', {
        target: backupTarget,
        includeEnv: siteBackupIncludeEnv,
        notes: siteBackupNotes
      });
      
      if (response.success && response.data) {
        // 立即添加一条"备份中"的记录到列表
        const processingBackup: BackupFile = {
          filename: response.data.filename,
          size: 0, // 暂时未知
          createdAt: response.data.createdAt || new Date().toISOString(),
          type: 'manual',
          backupType: 'site',
          status: 'processing',
          notes: siteBackupNotes
        };
        
        setBackups(prev => [processingBackup, ...prev]);
        
        message.success({
          content: '站点备份任务已启动！备份将在后台执行（需1-2分钟）',
          duration: 8,
        });
        
        setSiteBackupIncludeEnv(false);
        setSiteBackupNotes('');
        
        // 延迟60秒后刷新备份列表（站点备份需要更长时间）
        setTimeout(() => {
          fetchBackups();
        }, 60000);
      } else {
        message.error(response.message || '站点备份失败');
      }
    } catch (error: any) {
      message.error('站点备份失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDbStats();
    fetchBackups();
    loadAutoBackupSettings();
  }, []);

  // 加载自动备份设置
  const loadAutoBackupSettings = async () => {
    try {
      const response = await apiClient.get('/admin/database/auto-backup-settings');
      if (response.success && response.data) {
        // 统一备份目标
        setBackupTarget(response.data.target || response.data.siteTarget || response.data.dbTarget || ['local', 'cloudflare']);
        
        // 站点备份配置
        setSiteAutoBackupEnabled(response.data.siteEnabled !== undefined ? response.data.siteEnabled : true);
        setSiteAutoBackupInterval(response.data.siteInterval || 'weekly');
        setSiteAutoBackupTime(response.data.siteTime || '03:00');
        setSiteAutoBackupWeekday(response.data.siteWeekday !== undefined ? response.data.siteWeekday : 0);
        
        // 数据库备份配置
        setDbAutoBackupEnabled(response.data.dbEnabled !== undefined ? response.data.dbEnabled : true);
        setDbAutoBackupInterval(response.data.dbInterval || 'daily');
        setDbAutoBackupTime(response.data.dbTime || '02:00');
        setDbAutoBackupWeekday(response.data.dbWeekday !== undefined ? response.data.dbWeekday : 0);
      }
    } catch (error) {
      console.error('加载自动备份设置失败:', error);
    }
  };

  // 统一保存所有备份配置
  const handleSaveAllBackupSettings = async () => {
    try {
      setLoading(true);
      
      if (backupTarget.length === 0) {
        message.warning('请至少选择一个备份目标');
        setLoading(false);
        return;
      }
      
      const response = await apiClient.post('/admin/database/auto-backup-settings', {
        target: backupTarget,
        siteEnabled: siteAutoBackupEnabled,
        siteInterval: siteAutoBackupInterval,
        siteTime: siteAutoBackupTime,
        siteWeekday: siteAutoBackupWeekday, // 周几备份（0=周日, 1=周一, ..., 6=周六）
        dbEnabled: dbAutoBackupEnabled,
        dbInterval: dbAutoBackupInterval,
        dbTime: dbAutoBackupTime,
        dbWeekday: dbAutoBackupWeekday // 周几备份（0=周日, 1=周一, ..., 6=周六）
      });
      
      if (response.success) {
        message.success('备份配置已保存');
      } else {
        message.error(response.message || '保存失败');
      }
    } catch (error: any) {
      message.error('保存失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 创建数据库备份（使用配置的目标）
  const handleCreateBackup = async () => {
    try {
      if (backupTarget.length === 0) {
        message.warning('请先选择备份目标');
        return;
      }
      
      setLoading(true);
      const response = await apiClient.post('/admin/database/backup', {
        target: backupTarget
      });
      
      if (response.success && response.data) {
        // 立即添加一条"备份中"的记录到列表
        const processingBackup: BackupFile = {
          filename: response.data.filename,
          size: 0, // 暂时未知
          createdAt: response.data.createdAt || new Date().toISOString(),
          type: 'manual',
          backupType: 'database',
          status: 'processing'
        };
        
        setBackups(prev => [processingBackup, ...prev]);
        
        message.success({
          content: '数据库备份任务已启动！备份将在后台执行',
          duration: 5,
        });
        
        // 延迟8秒后刷新备份列表（给后台足够时间完成）
        setTimeout(() => {
          fetchBackups();
        }, 8000);
      } else {
        message.error(response.message || '备份失败');
      }
    } catch (error: any) {
      message.error('备份失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 还原备份
  const handleRestore = async (filename: string) => {
    try {
      setLoading(true);
      const response = await apiClient.post('/admin/database/restore', { filename });
      
      if (response.success) {
        message.success('数据库还原成功！请重新登录');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        message.error(response.message || '还原失败');
      }
    } catch (error: any) {
      message.error('还原失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 下载备份
  const handleDownload = async (filename: string, backupType?: string) => {
    try {
      const endpoint = backupType === 'site' 
        ? `/admin/site/download/${filename}`
        : `/admin/database/download/${filename}`;
      
      const response: any = await apiClient.get(endpoint, {
        responseType: 'blob'
      });
      
      // 创建下载链接
      const blob = response instanceof Blob ? response : new Blob([response]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      message.success('备份文件下载成功');
    } catch (error: any) {
      message.error('下载失败: ' + (error.message || '未知错误'));
    }
  };

  // 删除备份
  const handleDelete = async (filename: string, backupType?: string) => {
    try {
      setLoading(true);
      const endpoint = backupType === 'site'
        ? `/admin/site/backup/${filename}`
        : `/admin/database/backup/${filename}`;
      
      const response = await apiClient.delete(endpoint);
      
      if (response.success) {
        message.success('备份已删除');
        fetchBackups();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error: any) {
      message.error('删除失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 导出数据为JSON
  const handleExportJSON = async () => {
    try {
      setLoading(true);
      message.loading('正在导出数据...', 0);
      
      const response = await apiClient.get('/admin/database/export');
      
      message.destroy();
      
      if (response.success) {
        // 创建JSON文件下载
        const dataStr = JSON.stringify(response.data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `data-export-${new Date().toISOString().substring(0, 19).replace(/[:.]/g, '-')}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        message.success('数据导出成功！');
      } else {
        message.error(response.message || '导出失败');
      }
    } catch (error: any) {
      message.destroy();
      message.error('导出失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 切换备份目标
  const toggleBackupTarget = (target: string) => {
    if (backupTarget.includes(target)) {
      const newTargets = backupTarget.filter(t => t !== target);
      setBackupTarget(newTargets);
      message.info(`已取消选择: ${getTargetName(target)}`);
      } else {
      const newTargets = [...backupTarget, target];
      setBackupTarget(newTargets);
      message.success(`已选择: ${getTargetName(target)}`);
    }
  };
  
  const getTargetName = (target: string) => {
    switch (target) {
      case 'local': return '本地';
      case 'cloudflare': return 'R2';
      case 'tencent': return 'COS';
      default: return target;
    }
  };

  // 备份列表表格列
  const columns = [
    {
      title: '备份类型',
      dataIndex: 'backupType',
      key: 'backupType',
      width: 100,
      filters: [
        { text: '数据库备份', value: 'database' },
        { text: '站点备份', value: 'site' },
      ],
      onFilter: (value: any, record: BackupFile) => record.backupType === value,
      render: (backupType: string) => (
        backupType === 'site' ? (
          <Tag color="purple" icon={<CloudServerOutlined />}>站点</Tag>
        ) : (
          <Tag color="blue" icon={<DatabaseOutlined />}>数据库</Tag>
        )
      ),
    },
    {
      title: '备份文件',
      dataIndex: 'filename',
      key: 'filename',
      render: (filename: string, record: BackupFile) => {
        // 解析文件名中的位置信息（新格式）
        const locationMatch = filename.match(/site-backup-.*?-(local|r2|cos|local-r2|local-cos|r2-cos|local-r2-cos)-/);
        const locations = locationMatch ? locationMatch[1].split('-') : ['local'];
        
        return (
          <div>
            <Space size="small">
            {record.location === 'cloudflare' && <CloudServerOutlined className="text-blue-500" />}
            {record.location === 'tencent' && <CloudServerOutlined className="text-green-500" />}
            {(!record.location || record.location === 'local') && <FileTextOutlined />}
              <span className="font-mono text-xs">{filename}</span>
              {record.status === 'processing' && (
                <Tag color="processing" icon={<ReloadOutlined spin />} className="text-xs">备份中</Tag>
              )}
          </Space>
            <div className="mt-1">
              {record.type === 'auto' && <Tag color="blue" className="text-xs">自动</Tag>}
              {record.type === 'manual' && <Tag color="green" className="text-xs">手动</Tag>}
              {record.location === 'cloudflare' && <Tag color="blue" className="text-xs">R2</Tag>}
              {record.location === 'tencent' && <Tag color="green" className="text-xs">COS</Tag>}
              {record.location === 'local' && <Tag className="text-xs">本地</Tag>}
              
              {/* 显示文件名中的位置信息 */}
              {record.backupType === 'site' && locations.length > 1 && (
                <div className="mt-1">
                  {locations.map((loc: string) => (
                    <Tag key={loc} color={loc === 'r2' ? 'blue' : loc === 'cos' ? 'green' : 'default'} className="text-xs">
                      {loc === 'r2' ? 'R2' : loc === 'cos' ? 'COS' : '本地'}
                    </Tag>
                  ))}
                </div>
              )}
            </div>
            {record.notes && (
              <div className="text-xs text-gray-500 mt-1">📝 {record.notes}</div>
            )}
          </div>
        );
      },
    },
    {
      title: '大小',
      dataIndex: 'size',
      key: 'size',
      width: 120,
      render: (size: number, record: BackupFile) => (
        record.status === 'processing' ? (
          <Tag color="processing" icon={<ReloadOutlined spin />}>处理中</Tag>
        ) : (
          <span>{size ? (size / 1024 / 1024).toFixed(2) + ' MB' : '-'}</span>
        )
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date: string) => new Date(date).toLocaleString('zh-CN', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }),
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_: any, record: BackupFile) => {
        const isProcessing = record.status === 'processing';
        
        return (
          <Space>
            <Tooltip title={isProcessing ? '备份中，请稍候' : '下载备份'}>
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(record.filename, record.backupType)}
                disabled={isProcessing}
              >
                下载
              </Button>
            </Tooltip>
            {record.backupType === 'database' && (
            <Popconfirm
              title="确认还原此备份？"
              description="此操作将覆盖当前数据库，建议先创建备份"
              onConfirm={() => handleRestore(record.filename)}
              okText="确认还原"
              cancelText="取消"
              disabled={isProcessing}
            >
              <Tooltip title={isProcessing ? '备份中，请稍候' : '还原数据库'}>
                <Button
                  type="link"
                  size="small"
                  icon={<HistoryOutlined />}
                  disabled={isProcessing}
                >
                  还原
                </Button>
              </Tooltip>
            </Popconfirm>
            )}
            <Popconfirm
              title="确认删除此备份？"
              description="删除后无法恢复"
              onConfirm={() => handleDelete(record.filename, record.backupType)}
              okText="确认删除"
              cancelText="取消"
              disabled={isProcessing}
            >
              <Tooltip title={isProcessing ? '备份中，请稍候' : '删除备份'}>
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={isProcessing}
                >
                  删除
                </Button>
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];


  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <SaveOutlined className="text-2xl text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-800">数据备份</h2>
        </div>
        <Button 
          type="primary"
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={loading}
        >
          刷新
        </Button>
      </div>

      <Alert
        message="数据备份说明"
        description="支持站点备份和数据库备份，默认保存到本地+云端双重保障。自动保留最新的3个备份文件，过期文件自动清理。"
        type="info"
        showIcon
        closable
      />


      {/* 数据库概览 - 简洁卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card size="small" className="text-center hover:shadow-md transition-shadow">
          <div className="text-2xl font-bold text-blue-600">{dbStats?.users || 0}</div>
          <div className="text-gray-600 text-sm">用户总数</div>
        </Card>
        <Card size="small" className="text-center hover:shadow-md transition-shadow">
          <div className="text-2xl font-bold text-purple-600">{(dbStats?.videoGenerations || 0) + (dbStats?.imageGenerations || 0)}</div>
          <div className="text-gray-600 text-sm">生成记录</div>
        </Card>
        <Card size="small" className="text-center hover:shadow-md transition-shadow">
          <div className="text-2xl font-bold text-green-600">{dbStats?.payments || 0}</div>
          <div className="text-gray-600 text-sm">支付记录</div>
        </Card>
        <Card size="small" className="text-center hover:shadow-md transition-shadow">
          <div className="text-2xl font-bold text-orange-600">
            {dbStats?.size ? `${(dbStats.size / 1024 / 1024).toFixed(1)}MB` : '-'}
          </div>
          <div className="text-gray-600 text-sm">数据库大小</div>
        </Card>
      </div>

      {/* 左右布局：站点备份 + 数据库备份 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* 左侧：站点备份 */}
      <Card 
          className="shadow-md"
        title={
          <div className="flex items-center space-x-2">
              <CloudServerOutlined className="text-purple-600" />
              <span>完整站点备份</span>
          </div>
        }
        extra={
            <span className="text-xs text-gray-500">源码 + 数据库 + 文件</span>
          }
        >
          {/* 手动备份 */}
          <div className="mb-4">
            <Divider orientation="left" className="text-sm">手动备份</Divider>
          <Button
            type="primary"
            size="large"
            icon={<CloudServerOutlined />}
              onClick={handleSiteBackup}
            loading={loading}
            block
          >
            创建站点备份
          </Button>
        </div>
          
          {/* 自动备份设置 */}
          <div>
            <Divider orientation="left" className="text-sm">
          <div className="flex items-center space-x-2">
                <span>自动备份</span>
                <Switch
                  checked={siteAutoBackupEnabled}
                  onChange={setSiteAutoBackupEnabled}
                  size="small"
                />
              </div>
            </Divider>
            
            {siteAutoBackupEnabled && (
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-600">频率:</span>
                  <Space.Compact>
            <Button
                      size="small"
                      type={siteAutoBackupInterval === 'daily' ? 'primary' : 'default'}
                      onClick={() => setSiteAutoBackupInterval('daily')}
                    >
                      每天
            </Button>
            <Button
                      size="small"
                      type={siteAutoBackupInterval === 'weekly' ? 'primary' : 'default'}
                      onClick={() => setSiteAutoBackupInterval('weekly')}
                    >
                      每周
            </Button>
                  </Space.Compact>
          </div>
                
                {/* 每周备份：选择周几 */}
                {siteAutoBackupInterval === 'weekly' && (
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600">周几:</span>
                    <Space.Compact>
                      {['日', '一', '二', '三', '四', '五', '六'].map((day, index) => (
                        <Button
                          key={index}
                          size="small"
                          type={siteAutoBackupWeekday === index ? 'primary' : 'default'}
                          onClick={() => setSiteAutoBackupWeekday(index)}
                        >
                          周{day}
                        </Button>
                      ))}
                    </Space.Compact>
                  </div>
                )}
                
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-600">时间:</span>
                  <TimePicker
                    size="small"
                    format="HH:mm"
                    value={dayjs(siteAutoBackupTime, 'HH:mm')}
                    onChange={(time) => {
                      if (time) {
                        setSiteAutoBackupTime(time.format('HH:mm'));
                      }
                    }}
                  />
                </div>
                
              </div>
                )}
              </div>
        </Card>

        {/* 右侧：数据库备份 */}
        <Card 
          className="shadow-md"
          title={
            <div className="flex items-center space-x-2">
              <DatabaseOutlined className="text-blue-600" />
              <span>数据库备份</span>
            </div>
          }
          extra={
            <span className="text-xs text-gray-500">仅备份数据</span>
          }
        >
          {/* 手动备份 */}
          <div className="mb-4">
            <Divider orientation="left" className="text-sm">手动备份</Divider>
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="primary"
                size="large"
                icon={<SaveOutlined />}
                onClick={handleCreateBackup}
                loading={loading}
                block
              >
                立即备份
              </Button>
              <Button
                size="large"
                icon={<CloudDownloadOutlined />}
                onClick={handleExportJSON}
                loading={loading}
                block
              >
                导出JSON
              </Button>
                </div>
          </div>
          
          {/* 自动备份设置 */}
          <div>
            <Divider orientation="left" className="text-sm">
                  <div className="flex items-center space-x-2">
                <span>自动备份</span>
                <Switch
                  checked={dbAutoBackupEnabled}
                  onChange={setDbAutoBackupEnabled}
                  size="small"
                />
              </div>
            </Divider>
            
            {dbAutoBackupEnabled && (
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-600">频率:</span>
                    <Space.Compact>
                      <Button
                        size="small"
                      type={dbAutoBackupInterval === 'daily' ? 'primary' : 'default'}
                      onClick={() => setDbAutoBackupInterval('daily')}
                      >
                        每天
                      </Button>
                      <Button
                        size="small"
                      type={dbAutoBackupInterval === 'weekly' ? 'primary' : 'default'}
                      onClick={() => setDbAutoBackupInterval('weekly')}
                      >
                        每周
                      </Button>
                    </Space.Compact>
                  </div>
                  
                {/* 每周备份：选择周几 */}
                {dbAutoBackupInterval === 'weekly' && (
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600">周几:</span>
                    <Space.Compact>
                      {['日', '一', '二', '三', '四', '五', '六'].map((day, index) => (
                        <Button
                          key={index}
                          size="small"
                          type={dbAutoBackupWeekday === index ? 'primary' : 'default'}
                          onClick={() => setDbAutoBackupWeekday(index)}
                        >
                          周{day}
                        </Button>
                      ))}
                    </Space.Compact>
                  </div>
                )}
                  
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600">时间:</span>
                      <TimePicker
                        size="small"
                        format="HH:mm"
                      value={dayjs(dbAutoBackupTime, 'HH:mm')}
                        onChange={(time) => {
                          if (time) {
                          setDbAutoBackupTime(time.format('HH:mm'));
                          }
                        }}
                      />
                </div>
              
              </div>
                )}
              </div>
        </Card>
                </div>

      {/* 备份目标小按钮 */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center space-x-3">
            <SaveOutlined className="text-indigo-600" />
            <span className="font-semibold text-gray-700">备份目标:</span>
                    <Space.Compact>
                      <Button
                        size="small"
                type={backupTarget.includes('local') ? 'primary' : 'default'}
                icon={backupTarget.includes('local') ? <CheckCircleOutlined /> : <DatabaseOutlined />}
                onClick={() => toggleBackupTarget('local')}
                      >
                本地
                      </Button>
                      <Button
                        size="small"
                type={backupTarget.includes('cloudflare') ? 'primary' : 'default'}
                icon={backupTarget.includes('cloudflare') ? <CheckCircleOutlined /> : <CloudServerOutlined />}
                onClick={() => toggleBackupTarget('cloudflare')}
                      >
                R2
                      </Button>
                      <Button
                        size="small"
                type={backupTarget.includes('tencent') ? 'primary' : 'default'}
                icon={backupTarget.includes('tencent') ? <CheckCircleOutlined /> : <CloudUploadOutlined />}
                onClick={() => toggleBackupTarget('tencent')}
                      >
                COS
                      </Button>
                    </Space.Compact>
                  </div>
          <Button
            type="primary"
            size="large"
            icon={<SaveOutlined />}
            onClick={handleSaveAllBackupSettings}
            loading={loading}
          >
            💾 保存所有备份配置
          </Button>
                    </div>
                </div>

      {/* 备份列表 */}
      <Card
        title={
          <span>
            <HistoryOutlined className="mr-2" />
            备份历史 ({backups.length})
          </span>
        }
        extra={
          <Space>
            <span className="text-sm text-gray-500">
              共 {backups.length} 个备份
            </span>
          </Space>
        }
        className="shadow-md"
      >
        {backups.length === 0 ? (
          <div className="text-center py-12">
            <DatabaseOutlined className="text-6xl text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">暂无备份文件</p>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleCreateBackup}
              loading={loading}
            >
              创建第一个备份
            </Button>
          </div>
        ) : (
          <Table
            dataSource={backups}
            columns={columns}
            rowKey="filename"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 个备份`
            }}
          />
        )}
      </Card>

    </div>
  );
};

export default DatabaseManager;

